<script lang="ts">
import { computed } from 'vue';
import Icon from '../Icon/Icon.vue';
import Snackbar from '../Snackbar/Snackbar.vue';

import type { MessageType, SnackbarButton } from '../../types/snackbar.ts';
import type { NotificationPattern } from '../../types/notifications.ts';

type Props = {
  type?: MessageType;
  message?: string;
  buttons?: SnackbarButton[];
  open: boolean;
  pattern?: NotificationPattern;
  duration?: number;
  id?: string;
};
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  pattern: 'inline',
  duration: 5000,
});

const emit = defineEmits<{
  buttonClicked: [button: SnackbarButton];
  close: [];
  'update:open': [value: boolean];
}>();

const icon = computed(() => {
  if (props.type === 'warning') {
    return 'warning';
  } else if (props.type === 'error') {
    return 'error';
  } else if (props.type === 'success') {
    return 'check_circle';
  } else {
    return 'info';
  }
});

const close = () => {
  emit('update:open', false);
  emit('close');
};

const handleButtonClicked = (button: SnackbarButton) => {
  emit('buttonClicked', button);
};
</script>

<template>
  <Snackbar
    v-if="pattern === 'persistent' || pattern === 'toast'"
    :buttons="buttons"
    :duration="duration"
    :message="message"
    :open="open"
    :pattern="pattern"
    :type="type"
    @button-clicked="handleButtonClicked"
    @close="close"
    @update:open="emit('update:open', $event)"
  >
    <slot />
  </Snackbar>

  <div
    v-else-if="open"
    :id="id"
    aria-atomic="true"
    :class="[
      $style.container,
      type === 'info' && $style.info,
      type === 'warning' && $style.warning,
      type === 'error' && $style.error,
      type === 'success' && $style.success,
    ]"
    role="alert"
  >
    <div :class="$style.content">
      <Icon aria-hidden="true" :class="$style.icon" :name="icon" />

      <p v-if="message" :class="$style.message">
        {{ message }}
      </p>

      <p v-else :class="$style.message">
        <slot />
      </p>
    </div>
  </div>
</template>

<style lang="scss" module src="./Notifications.scss" />
